import React, {useMemo, useState} from 'react'
import SearchPropertyFeatures from './SearchPropertyFeatures'
import {PropertyConfig} from '../../schema'
import {booking} from '@whyhotel/common-schema'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faLocationCrosshairs} from '@fortawesome/free-solid-svg-icons'
import ImageCarousel from '../common/ImageCarousel'
import SearchPropertyDetailsModal from './SearchPropertyDetailsModal'
import clsx from 'clsx'
import {getLogger} from '../../services/logging'
import {formatCurrency} from '../../services/utils'
import {pushDataLayerEvent} from '../../services/dataLayer'
import {getLowestRateForProperty} from '../../services/offerUtils'

const logger = getLogger('SearchPropertyResultItem')

export interface SearchPropertyResultsItemProps {
  result: booking.UnitGroupOfferResult
  propertyConfig: PropertyConfig
  onSelectProperty: (propertyId: string) => void
  onShowOnMap?: (propertyId: string) => void
  className?: string
}

const SearchPropertyResultItem = ({
  result,
  propertyConfig,
  onSelectProperty,
  onShowOnMap = () => {},
  className = ''
}: SearchPropertyResultsItemProps) => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const {propertyId, unitGroupOffers} = result

  const lowestRate = getLowestRateForProperty(unitGroupOffers)

  const coordinates = propertyConfig.coordinates
  const hasValidCoordinates =
    coordinates && coordinates.latitude !== 0 && coordinates.longitude !== 0

  const handleShowDetailsModal = () => {
    pushDataLayerEvent({
      event: 'Property_Details_View',
      category: 'IBE',
      value: 1
    })
    logger.info({message: 'Opening property details modal...'})
    setIsDetailModalOpen(true)
  }

  const handleShowMapModal = () => {
    pushDataLayerEvent({
      event: 'Property_Map_Show',
      category: 'IBE',
      value: 1
    })
    onShowOnMap(propertyConfig.propertyApaleoId)
  }

  return (
    <div
      className={className}
      data-property-id={propertyConfig?.propertyApaleoId}
    >
      <div className="card bg-white shadow">
        <div className="flex flex-col md:flex-row">
          {/* image carousel */}
          <div
            className={clsx(
              'w-full overflow-hidden rounded-tl-2xl rounded-tr-2xl h-72',
              'md:w-5/12 md:rounded-bl-2xl md:rounded-tr-none md:h-auto md:max-h-[600px]',
              'relative'
            )}
          >
            <ImageCarousel images={propertyConfig.images} />

            {propertyConfig.isPopup && (
              <span className="bg-orange-600 text-xs text-white font-medium absolute px-2 py-1 top-6 rounded-r">
                pop-up hotel
              </span>
            )}
          </div>

          {/* card body */}
          <div className="w-full md:w-7/12 flex flex-col p-6">
            {/* property header */}
            <h1 className="text-xl font-semibold">{propertyConfig.name}</h1>
            {/* property neighborhood/city */}
            <div className="text-sm text-light-primary text-pretty mb-2">
              {propertyConfig.neighborhood && (
                <span>{propertyConfig.neighborhood} &#x2022; </span>
              )}
              <span>{propertyConfig.location}</span>
            </div>
            {/* property amenities */}
            {propertyConfig.highlightedFeatures && (
              <SearchPropertyFeatures
                features={propertyConfig.highlightedFeatures}
                className="mb-4"
              />
            )}

            {/* property description */}
            <div className="text-sm text-light-primary mb-4">
              {propertyConfig.description}
            </div>

            {/* property details modal link */}
            <a
              className={clsx(
                'link text-sm text-accent font-semibold',
                'no-underline hover:underline',
                'mb-4'
              )}
              onClick={handleShowDetailsModal}
              onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                if (e.key === 'Enter') {
                  handleShowDetailsModal()
                }
              }}
              tabIndex={0}
              aria-label="View all property features."
            >
              View all property features
            </a>

            <div className="flex flex-row justify-end mb-2">
              {/* property pricing */}
              <div className="flex flex-col justify-center items-center flex-none">
                <div className="text-gray-500 text-2xs leading-3">
                  starting at
                </div>
                <div className="font-semibold flex flex-row items-center">
                  <span className="text-xl leading-6 mr-1">
                    {formatCurrency(lowestRate.nightly)}
                  </span>
                  <span className="text-xs uppercase">
                    {lowestRate.currency}
                  </span>
                </div>
                <div className="text-gray-500 text-2xs leading-3">
                  per night
                </div>
                <div className="text-gray-500 text-2xs leading-3">
                  plus taxes & fees
                </div>
              </div>
            </div>

            <div
              className={clsx('flex flex-row items-center mt-2 w-full', {
                'justify-between': hasValidCoordinates,
                'justify-end': !hasValidCoordinates
              })}
            >
              {/* property map link */}
              {hasValidCoordinates && (
                <div className="flex-0 flex flex-row items-center gap-4">
                  <a
                    className={clsx(
                      'link text-sm font-medium no-underline hover:underline',
                      'flex flex-row text-sm items-center gap-1'
                    )}
                    onClick={handleShowMapModal}
                    onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                      if (e.key === 'Enter') {
                        handleShowMapModal()
                      }
                    }}
                    tabIndex={0}
                    aria-label="View property location on map."
                  >
                    <FontAwesomeIcon
                      icon={faLocationCrosshairs}
                      className="text-accent"
                      title="View property location on map."
                    />
                    <span className="text-sm xl:hidden">view map</span>
                    <span className="text-sm hidden xl:inline">
                      view on map
                    </span>
                  </a>
                </div>
              )}

              {/* property select cta */}
              <button
                className="btn btn-secondary btn-sm sm:btn-md"
                onClick={() => {
                  pushDataLayerEvent({
                    event: 'Property_Select',
                    category: 'IBE',
                    value: 1
                  })
                  logger.info({
                    message: 'User selected property',
                    data: {propertyId}
                  })
                  if (propertyId) {
                    onSelectProperty(propertyId)
                  }
                }}
                aria-label="Select this property and see which rooms are available."
              >
                see what's available
              </button>
            </div>
          </div>
        </div>
      </div>

      <SearchPropertyDetailsModal
        propertyConfig={propertyConfig}
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
      />
    </div>
  )
}

export default SearchPropertyResultItem
